import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { AnimatePresence, motion } from 'motion/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../button'
import { TextField } from '../inputs'

export type ConfirmationDialogRef = {
	confirm: (options?: {
		title?: React.ReactNode
		message?: React.ReactNode
		confirmText?: string
		cancelText?: string
		destructive?: boolean
		requireConfirmationText?: string
	}) => Promise<void>
}

export interface ConfirmationDialogProps {
	title?: React.ReactNode
	message?: React.ReactNode
	confirmText?: string
	cancelText?: string
	ref: React.Ref<ConfirmationDialogRef>
}

export function ConfirmationDialog({
	title: defaultTitle,
	message: defaultMessage,
	confirmText: defaultConfirmText,
	cancelText: defaultCancelText,
	ref,
}: ConfirmationDialogProps) {
	const { t } = useTranslation('common')
	const resolvedConfirmText = defaultConfirmText ?? t('common.confirm')
	const resolvedCancelText = defaultCancelText ?? t('common.cancel')
	const [isOpen, setIsOpen] = React.useState(false)
	const [title, setTitle] = React.useState<React.ReactNode>(defaultTitle)
	const [message, setMessage] = React.useState<React.ReactNode>(defaultMessage)
	const [confirmText, setConfirmText] = React.useState(resolvedConfirmText)
	const [cancelText, setCancelText] = React.useState(resolvedCancelText)
	const [destructive, setDestructive] = React.useState(false)
	const [requireConfirmationText, setRequireConfirmationText] = React.useState<string | null>(null)
	const [typedConfirmation, setTypedConfirmation] = React.useState('')

	const resolverRef = React.useRef<
		{
			resolve: () => void
			reject: (reason?: unknown) => void
		} | null
	>(null)

	React.useImperativeHandle(ref, () => ({
		confirm: (options) => {
			if (options) {
				setTitle(options.title ?? defaultTitle)
				setMessage(options.message ?? defaultMessage)
				setConfirmText(options.confirmText ?? resolvedConfirmText)
				setCancelText(options.cancelText ?? resolvedCancelText)
				setDestructive(options.destructive ?? false)
				setRequireConfirmationText(options.requireConfirmationText ?? null)
			}
			setTypedConfirmation('')
			setIsOpen(true)
			return new Promise<void>((resolve, reject) => {
				resolverRef.current = { resolve, reject }
			})
		},
	}))

	const isConfirmDisabled = requireConfirmationText !== null
		&& typedConfirmation.trim() !== requireConfirmationText

	const handleConfirm = React.useCallback(() => {
		const current = resolverRef.current
		resolverRef.current = null
		setIsOpen(false)
		current?.resolve()
	}, [])

	const handleCancel = React.useCallback(() => {
		const current = resolverRef.current
		resolverRef.current = null
		setIsOpen(false)
		current?.reject(new Error('Cancelled'))
	}, [])

	return (
		<AnimatePresence mode="popLayout">
			{isOpen && (
				<Dialog static={true} open={isOpen} onClose={handleCancel} className="relative z-50">
					<motion.div
						initial={{ backgroundColor: 'rgba(0, 0, 0, 0)', backdropFilter: 'blur(0px)' }}
						animate={{ backgroundColor: 'rgba(0, 0, 0, 0.2)', backdropFilter: 'blur(8px)' }}
						exit={{ backgroundColor: 'rgba(0, 0, 0, 0)', backdropFilter: 'blur(0px)' }}
						transition={{ duration: 0.2 }}
						className="fixed inset-0"
						data-is="confirmation-dialog"
					/>
					<div className="fixed inset-0 w-screen overflow-y-auto">
						<div className="flex min-h-full items-center justify-center p-4">
							<DialogPanel
								as={motion.div}
								initial={{
									opacity: 0,
									scale: 0.95,
									transition: { duration: 0.2 },
								}}
								animate={{
									opacity: 1,
									scale: 1,
									transition: { duration: 0.2 },
								}}
								exit={{
									opacity: 0,
									scale: 0.95,
									transition: { duration: 0.2 },
								}}
								className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg ring-1 ring-grey-200/50"
							>
								<DialogTitle className="text-lg font-medium text-grey-900 mb-2">
									{title}
								</DialogTitle>
								<p className="text-sm text-grey-700 mb-6">{message}</p>
								{requireConfirmationText !== null && (
									<div className="mb-6">
										<TextField
											label={t('common.typeToConfirm', { text: requireConfirmationText })}
											value={typedConfirmation}
											onChange={setTypedConfirmation}
											placeholder={requireConfirmationText}
											onKeyDown={(event) => {
												if (event.key === 'Enter' && !isConfirmDisabled) {
													handleConfirm()
												}
											}}
										/>
									</div>
								)}
								<div className="flex gap-3 justify-end">
									{cancelText && (
										<Button type="subtle" size="normal" onClick={handleCancel}>
											{cancelText}
										</Button>
									)}
									<Button
										type={destructive ? 'destructive' : 'committal'}
										size="normal"
										onClick={handleConfirm}
										disabled={isConfirmDisabled}
										cursor={isConfirmDisabled ? 'not-allowed' : 'pointer'}
									>
										{confirmText}
									</Button>
								</div>
							</DialogPanel>
						</div>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	)
}
